/**
 * Gross-margin arithmetic shared by the product form and the Cost Calculator.
 *
 * Margin is measured against the selling price (margin = (price − cost) / price),
 * so the price at a given margin is cost / (1 − margin). This is not the same as
 * markup, which is measured against cost — a 20% margin on $20.42 is $25.53,
 * whereas a 20% markup is $24.50. The distinction matters: pricing at markup
 * when you meant margin leaves money on the table on every unit sold.
 */

/** Selling price that yields `margin` percent gross margin on `cost`. */
export function priceAtMargin(cost: number, margin: number): number | null {
  if (!Number.isFinite(margin) || margin < 0 || margin >= 100) return null;
  return cost / (1 - margin / 100);
}

/**
 * Gross margin percent for a cost/price pair, or null when either side is
 * missing or the price is zero (division by zero). Negative when selling
 * below cost — callers decide how loudly to flag that.
 */
export function grossMarginPct(cost: number, price: number): number | null {
  if (!Number.isFinite(cost) || !Number.isFinite(price) || cost < 0 || price <= 0) return null;
  return ((price - cost) / price) * 100;
}
